import React, { useState, useContext } from 'react';
import { authService } from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { jwtDecode } from 'jwt-decode';
import { Button, TextField, Box, Typography, Link as MuiLink, Alert, CircularProgress, Divider } from '@mui/material';
import { useNavigate, Link, useLocation } from 'react-router-dom';

export default function Login() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await authService.post('/auth/login', form);
      const token = response.data.jwtToken;

      if (token) {
        const decodedToken = jwtDecode(token);
        login(token, decodedToken.role, decodedToken.sub);
        navigate(from, { replace: true });
      } else {
        setError('Login failed: No token received.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            eYantra EMS
        </Typography>
        <Typography variant="h5" component="h2" sx={{ color: 'text.secondary', mb: 3 }}>
            Login
        </Typography>
        {error && <Alert severity="error" sx={{width: '100%', mb: 2}}>{error}</Alert>}
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <TextField
              fullWidth
              id="username"
              name="username"
              label="Username or Email"
              value={form.username}
              onChange={handleChange}
              required
              margin="normal"
            />
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Password"
              type="password"
              value={form.password}
              onChange={handleChange}
              required
              margin="normal"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isLoading}
              sx={{ mt: 2, py: 1.5 }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>
        </form>

        <Typography variant="body2" sx={{ mt: 2 }}>
            <MuiLink component={Link} to="/forgot-password" color="inherit">
                Forgot Password?
            </MuiLink>
        </Typography>

        <Divider sx={{ my: 2, width: '100%' }}>
            <Typography variant="caption" sx={{color: 'text.secondary'}}>OR</Typography>
        </Divider>

        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          Don't have an account?{' '}
          <MuiLink component={Link} to="/register" color="primary" sx={{fontWeight: 600}}>
            Register here
          </MuiLink>
        </Typography>
    </Box>
  );
}